import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatEUR, firstCategoryWithProducts } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Star, ChevronDown, Ticket, Printer, Search } from "lucide-react";
import ScontrinoPreview from "@/components/ScontrinoPreview";
import OperatorSwitcher from "@/components/OperatorSwitcher";
import LotteryCodeInput from "@/components/LotteryCodeInput";
import CustomerLoyaltyPicker from "@/components/CustomerLoyaltyPicker";
import GestionaleLongPress from "@/components/pos/GestionaleLongPress";
import PrecontoButton from "@/components/pos/PrecontoButton";
import CheckoutKeypad, { ClearAllButton } from "@/components/pos/CheckoutKeypad";
import SwipeCartLine from "@/components/pos/SwipeCartLine";
import CheckoutPayments from "@/components/pos/CheckoutPayments";
import { lineTotal } from "@/lib/escpos";
import { sendFiscalReceipt, sendFiscalDuplicate, fiscalErrorMessage, emitFiscalPaper } from "@/lib/fiscal";
import ErrorBoundary from "@/components/ErrorBoundary";
import StoreSwitcher from "@/components/StoreSwitcher";
import { getActiveStoreId } from "@/lib/storeContext";
import { newOfflineId } from "@/lib/offlineQueue";
import { recoverUnreachableSale, SERVER_ATTEMPT } from "@/lib/offlineSales";
import { checkStockAlert, beepOk, beepError } from "@/lib/stockAlert";
import LowStockPopup from "@/components/pos/LowStockPopup";

function POSNegozio({
  mode = "negozio",
  title = "Velta One · Negozio",
  testidPrefix = "neg",
  headerExtra = null,
  topStrip = null,
  cartHeaderExtra = null,
  productsArea = null,
  extras = [],
  fallbackCatalog = null,
  saleNote = null,
  autoQtyDiscount = null,
  innerRef = null,
  onAfterClose = null,
  onClearAll = null,
}) {
  const tp = testidPrefix;
  const [tenant, setTenant] = useState(null);
  const [cats, setCats] = useState([]);
  const [prods, setProds] = useState([]);
  const [activeCat, setActiveCat] = useState(null);
  const [lines, setLines] = useState([]);
  const [last, setLast] = useState(null);
  const [showScontrino, setShowScontrino] = useState(false);
  const [lotteryCode, setLotteryCode] = useState("");
  const [customer, setCustomer] = useState(null);
  const [redeemPoints, setRedeemPoints] = useState(0);
  const [discountPct, setDiscountPct] = useState(0);
  const [discountEur, setDiscountEur] = useState(0);
  const [precontoOrderId, setPrecontoOrderId] = useState(null);
  const [kpValue, setKpValue] = useState("");
  const [multiplier, setMultiplier] = useState(null);
  const [selectedLine, setSelectedLine] = useState(null);
  const [crmOpen, setCrmOpen] = useState(false);
  const [reprint, setReprint] = useState(null);
  const [query, setQuery] = useState("");
  const [lowPopup, setLowPopup] = useState(null);
  const shownLow = useRef(new Set());

  const reprintLast = async () => {
    let doc;
    try {
      const r = await api.get(`/reports/last-document?mode=${mode}`);
      doc = r.data;
    } catch {
      toast.error("Nessun documento emesso da ristampare");
      return;
    }
    // Scontrino fiscale: duplicato nativo sulla RT via stesso percorso HTTP (fpmate.cgi)
    if (doc.status === "closed_fiscal") {
      const res = await sendFiscalDuplicate();
      if (res.ok) {
        toast.success(`Duplicato scontrino #${doc.receipt_number} inviato alla fiscale "${res.printer}"${res.detail ? ` — ${res.detail}` : ""}`);
        return;
      }
      if (!["no_fiscal_printer", "no_driver", "no_duplicate"].includes(res.reason)) {
        toast.error(fiscalErrorMessage(res), { duration: 8000 });
      }
    }
    setReprint({ order: doc, fiscal: doc.status === "closed_fiscal" });
  };

  useEffect(() => {
    api.get("/tenant/me").then((r) => setTenant(r.data && typeof r.data === "object" ? r.data : {})).catch(() => setTenant({}));
    if (productsArea) return;
    const asList = (d) => (Array.isArray(d) ? d.filter((x) => x && typeof x === "object") : []);
    let cancel = false;
    Promise.all([
      api.get(`/categories?mode=${mode}`).then((r) => asList(r.data)).catch(() => []),
      api.get(`/products?mode=${mode}`).then((r) => asList(r.data)).catch(() => []),
    ]).then(([c, p]) => {
      if (cancel) return;
      if (!c.length && !p.length && fallbackCatalog?.cats?.length) {
        setCats(fallbackCatalog.cats);
        setProds(Array.isArray(fallbackCatalog.prods) ? fallbackCatalog.prods : []);
      } else {
        setCats(c);
        setProds(p);
      }
    });
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, fallbackCatalog]);

  useEffect(() => {
    if (activeCat == null && (Array.isArray(cats) ? cats : []).length && (Array.isArray(prods) ? prods : []).length) setActiveCat(firstCategoryWithProducts(cats, prods));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cats, prods]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (Array.isArray(prods) ? prods : []).filter((p) => {
      if (!p || typeof p !== "object") return false;
      if (p.visible === false) return false;
      if (q) {
        const name = (p.name || "").toLowerCase();
        const barcode = String(p.barcode || p.sku || "").toLowerCase();
        return name.includes(q) || (barcode && barcode.includes(q));
      }
      return p.category_id === activeCat;
    });
  }, [prods, activeCat, query]);
  const safeLines = Array.isArray(lines) ? lines : [];
  const subtotal = safeLines.reduce((s, l) => s + lineTotal(l), 0);
  const afterPct = discountPct > 0 ? subtotal * (1 - discountPct / 100) : subtotal;
  const total = Math.max(0, afterPct - (discountEur || 0));
  const itemCount = safeLines.reduce((s, l) => s + (Number(l?.qty) || 0), 0);
  const kpNumeric = parseFloat(kpValue || 0) || 0;

  const warnStock = (p, extraQty) => {
    if (!p || p.stock_limit == null || p.stock_current == null) return;
    const inCart = (Array.isArray(lines) ? lines : []).filter((l) => l.product_id === p.id).reduce((s, l) => s + (Number(l.qty) || 0), 0);
    const projected = p.stock_current - inCart - extraQty;
    const hit = checkStockAlert({ ...p, stock_current: projected });
    if (hit && !shownLow.current.has(p.id)) {
      shownLow.current.add(p.id);
      setLowPopup([{ product_id: p.id, product_name: p.name, qty: projected, limit: p.stock_limit }]);
    }
  };

  const addLine = (p, qty, priceOverride) => {
    const price = priceOverride ?? p.price;
    warnStock(p, qty);
    setLines((prev) => {
      const i = prev.findIndex((l) => l.product_id === p.id && l.price === price && !l.quick && !l.discount_pct && !l.discount_eur);
      if (i >= 0) { const u = [...prev]; u[i] = { ...u[i], qty: u[i].qty + qty }; return u; }
      return [...prev, { product_id: p.id, name: p.name, qty, price, vat: p.vat, category_id: p.category_id }];
    });
  };

  // Lettore codice a barre USB/Bluetooth: raffica di tasti (<100ms) terminata da Invio
  useEffect(() => {
    let buf = "";
    let lastT = 0;
    const onKey = (e) => {
      const tag = (document.activeElement?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return;
      const t = Date.now();
      if (e.key === "Enter") {
        if (buf.length >= 4 && t - lastT < 100) {
          const code = buf;
          e.preventDefault();
          api.get(`/products/barcode/${encodeURIComponent(code)}`)
            .then((r) => { addLine(r.data, 1); beepOk(); toast.success(`${r.data.name} — ${formatEUR(r.data.price)}`); })
            .catch(() => { beepError(); toast.error(`Nessun articolo con codice a barre ${code}`, { duration: 5000 }); });
        }
        buf = "";
        return;
      }
      if (e.key.length === 1) {
        if (t - lastT > 100) buf = "";
        buf += e.key;
        lastT = t;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sconto compleanno animale: applicato automaticamente quando si seleziona il cliente
  const bdayApplied = useRef(false);
  const skipAutoBirthdayRef = useRef(false);
  useEffect(() => {
    if (skipAutoBirthdayRef.current) {
      skipAutoBirthdayRef.current = false;
      return;
    }
    if (customer?.pet_birthday_today && customer?.birthday_discount_enabled && Number(customer.birthday_discount_pct) > 0) {
      const pct = Number(customer.birthday_discount_pct);
      setDiscountPct(pct);
      bdayApplied.current = true;
      toast.success(`Compleanno di ${customer.pet_name || customer.name}! Sconto ${pct}% applicato`, { duration: 6000 });
    } else if (!customer && bdayApplied.current) {
      setDiscountPct(0);
      bdayApplied.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer?.id]);

  const onProductTap = (p) => {
    const qty = multiplier || 1;
    const override = kpNumeric > 0 ? kpNumeric : null;
    addLine(p, qty, override);
    if (override) toast.success(`${p.name} a ${formatEUR(override)}${qty > 1 ? ` × ${qty}` : ""}`);
    else if (qty > 1) toast.success(`${qty} × ${p.name}`);
    if (multiplier) setMultiplier(null);
    if (override) setKpValue("");
  };

  const onCategoryTap = (c) => {
    if (kpNumeric > 0) {
      const qty = multiplier || 1;
      setLines((prev) => [...prev, { product_id: null, name: c.name, qty, price: kpNumeric, vat: 22, category_id: c.id, quick: true }]);
      setKpValue(""); setMultiplier(null);
      toast.success(`Vendita rapida ${c.name}: ${formatEUR(kpNumeric)}${qty > 1 ? ` × ${qty}` : ""}`);
      return;
    }
    setActiveCat(c.id);
  };

  const addFreeLine = (amount) => {
    setLines((prev) => [...prev, { product_id: null, name: "Prezzo libero", qty: 1, price: amount, vat: 22 }]);
    toast.success(`Aggiunto: Prezzo libero ${formatEUR(amount)}`);
  };

  const applyDiscountPct = (pct) => {
    const v = Math.min(100, Math.max(0, pct));
    if (selectedLine != null && lines[selectedLine]) {
      const name = lines[selectedLine].name;
      setLines((prev) => (Array.isArray(prev) ? prev : []).map((l, i) => (i === selectedLine ? { ...l, discount_pct: v, discount_eur: 0 } : l)));
      setSelectedLine(null);
      toast.success(`Sconto ${v.toFixed(1)}% su "${name}"`);
      return;
    }
    setDiscountPct(v);
    toast.success(v > 0 ? `Sconto ${v.toFixed(1)}% sul conto` : "Sconto rimosso");
  };

  const applyDiscountEur = (amount) => {
    if (selectedLine != null && lines[selectedLine]) {
      const l = lines[selectedLine];
      const cap = Math.min(amount, l.price * l.qty * (1 - (l.discount_pct || 0) / 100));
      setLines((prev) => (Array.isArray(prev) ? prev : []).map((li, i) => (i === selectedLine ? { ...li, discount_eur: Math.round(cap * 100) / 100, discount_pct: 0 } : li)));
      setSelectedLine(null);
      toast.success(`Sconto ${formatEUR(cap)} su "${l.name}"`);
      return;
    }
    const cap = Math.min(amount, afterPct);
    setDiscountEur(Math.round(cap * 100) / 100);
    toast.success(`Sconto ${formatEUR(cap)} sul conto`);
  };

  const onLineTap = (i) => setSelectedLine((sel) => (sel === i ? null : i));
  const removeLine = (i) => { setSelectedLine(null); setLines((prev) => (Array.isArray(prev) ? prev : []).filter((_, idx) => idx !== i)); };
  const incrementLine = (i) => setLines((prev) => (Array.isArray(prev) ? prev : []).map((l, idx) => (idx === i ? { ...l, qty: l.qty + 1 } : l)));

  // Servizi rapidi (es. Ricarica tel., Marca da bollo): importo dal tastierino + tocco
  const onExtraTap = (ex) => {
    if (kpNumeric <= 0) { toast.info(`Digita l'importo sul tastierino, poi tocca "${ex.label}"`); return; }
    const qty = multiplier || 1;
    setLines((prev) => [...prev, { product_id: null, name: ex.label, qty, price: kpNumeric, vat: ex.vat ?? 22, note: "servizio" }]);
    toast.success(`${ex.label}: ${formatEUR(kpNumeric)}${qty > 1 ? ` × ${qty}` : ""}`);
    setKpValue(""); setMultiplier(null);
  };

  // Sconto automatico per quantità (es. enoteca multi-bottiglia)
  useEffect(() => {
    if (!autoQtyDiscount) return;
    setDiscountPct(autoQtyDiscount(itemCount));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemCount]);

  // API imperativa per i wrapper (toelettatura/gelateria): manipolano il conto dall'esterno
  useEffect(() => {
    if (innerRef) innerRef.current = {
      setLines, setDiscountPct, setDiscountEur, setRedeemPoints, addLine, clearAll,
      setCustomer: (c, opts) => {
        if (opts?.skipBirthday) skipAutoBirthdayRef.current = true;
        setCustomer(c);
      },
      tapProduct: onProductTap,
      getLines: () => lines,
      getKeypad: () => ({ value: kpNumeric, multiplier }),
      clearKeypad: () => { setKpValue(""); setMultiplier(null); },
    };
  });

  const clearAll = () => {
    setLines([]); setDiscountPct(0); setDiscountEur(0); setCustomer(null); setRedeemPoints(0);
    setLotteryCode(""); setPrecontoOrderId(null); setKpValue(""); setMultiplier(null); setSelectedLine(null);
    shownLow.current = new Set();
    onClearAll?.();
    toast.success("Conto azzerato");
  };

  const saveOrderForGestionale = async () => {
    if (!lines.length) { toast.error("Nessun articolo"); return null; }
    try {
      if (precontoOrderId) {
        const r = await api.patch(`/orders/${precontoOrderId}`, { lines, discount_pct: discountPct || 0, discount_eur: discountEur || 0, customer_id: customer?.id || undefined, notes: saleNote || undefined });
        return r.data;
      }
      const r = await api.post("/orders", { mode, lines, discount_pct: discountPct || 0, discount_eur: discountEur || 0, store_id: getActiveStoreId() || null, customer_id: customer?.id || undefined, notes: saleNote || undefined });
      return r.data;
    } catch { toast.error("Errore salvataggio"); return null; }
  };
  const onGestionaleClosed = () => {
    onAfterClose?.({ gestionale: true });
    clearAll();
  };

  const close = async (method, breakdown, opts) => {
    if (!lines.length) { toast.error("Nessun articolo nel conto"); return; }
    let tendered = opts?.cash_tendered || null;
    if (!tendered && method === "cash" && kpNumeric > 0) {
      if (kpNumeric < total - 0.001) {
        toast.error(`Contanti insufficienti: ricevuti ${formatEUR(kpNumeric)}, totale ${formatEUR(total)}`);
        return;
      }
      tendered = kpNumeric;
    }
    const offlineId = newOfflineId();
    const closeBody = { payment_method: method, discount_pct: discountPct || 0, discount_eur: discountEur || 0 };
    if (breakdown) closeBody.payment_breakdown = breakdown;
    if (tendered) closeBody.cash_tendered = tendered;
    let oid = precontoOrderId || null;
    try {
      const payload = { mode, lines, payment_method: method, discount_pct: discountPct || 0, discount_eur: discountEur || 0, store_id: getActiveStoreId() || null, offline_id: offlineId };
      if (lotteryCode) payload.lottery_code = lotteryCode;
      if (customer) payload.customer_id = customer.id;
      if (saleNote) payload.notes = saleNote;
      if (precontoOrderId) {
        const upd = { lines, discount_pct: discountPct || 0, discount_eur: discountEur || 0 };
        if (saleNote) upd.notes = saleNote;
        if (lotteryCode) upd.lottery_code = lotteryCode;
        if (customer) upd.customer_id = customer.id;
        await api.patch(`/orders/${precontoOrderId}`, upd, SERVER_ATTEMPT);
        oid = precontoOrderId;
      } else {
        const r = await api.post("/orders", payload, SERVER_ATTEMPT);
        oid = r.data.id;
      }
      if (redeemPoints > 0) closeBody.redeem_points = redeemPoints;
      if (redeemPoints > 0) {
        const discountPreview = await api.get(`/customers/${customer.id}/redeem-preview`);
        const valuePerThr = discountPreview.data.value_per_threshold;
        const thr = discountPreview.data.threshold;
        const discount = (redeemPoints / thr) * valuePerThr;
        if (!window.confirm(`Applicare sconto fedeltà di ${discount.toFixed(2)}€ scalando ${redeemPoints} punti al cliente ${customer.name}?\n\nOK = applica sconto · Annulla = chiudi senza sconto`)) {
          closeBody.redeem_points = 0;
          toast.info("Sconto fedeltà non applicato");
        }
      }
      const c = await api.post(`/orders/${oid}/close-fiscal`, closeBody, SERVER_ATTEMPT);
      setLines([]); setLotteryCode(""); setDiscountPct(0); setDiscountEur(0); setCustomer(null); setRedeemPoints(0);
      setPrecontoOrderId(null); setKpValue(""); setMultiplier(null); setSelectedLine(null); setCrmOpen(false);
      shownLow.current = new Set();
      toast.success(`Scontrino #${c.data.receipt_number} (gestionale). Attendi la carta sulla Epson…`);
      if (Array.isArray(c.data?.low_stock) && c.data.low_stock.length) setLowPopup(c.data.low_stock);
      onAfterClose?.(c.data);
      await emitFiscalPaper({ tenant, order: c.data });
    } catch (e) {
      const off = await recoverUnreachableSale(e, {
        tenant, offline_id: offlineId, server_order_id: oid, mode, lines,
        payment_method: method, payment_breakdown: breakdown || null,
        discount_pct: discountPct || 0, discount_eur: discountEur || 0,
        customer_id: customer?.id, cash_tendered: tendered, lottery_code: lotteryCode || null,
        notes: saleNote || null,
        store_id: getActiveStoreId() || null, redeem_points: closeBody.redeem_points || 0,
      });
      if (off.printed) {
        setLines([]); setLotteryCode(""); setDiscountPct(0); setDiscountEur(0); setCustomer(null); setRedeemPoints(0);
        setPrecontoOrderId(null); setKpValue(""); setMultiplier(null); setSelectedLine(null); setCrmOpen(false);
        shownLow.current = new Set();
        onAfterClose?.(off.order);
        return;
      }
      if (off.handled) return;
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  return (
    <div className="h-screen bg-velta-bg flex flex-col overflow-hidden">
      <header className="h-12 border-b border-velta-border bg-white flex items-center px-4 gap-4 shrink-0">
        <Link to="/cassa" className="inline-flex items-center gap-2 text-velta-mute hover:text-velta-ink text-sm font-medium">
          <ArrowLeft className="w-4 h-4" /> Modalità
        </Link>
        <div className="h-6 w-px bg-velta-border" />
        <div className="font-heading font-semibold tracking-tight">{title}</div>
        {headerExtra}
        <div className="ml-auto flex items-center gap-2">
          <button
            data-testid={`${tp}-reprint-last`}
            onClick={reprintLast}
            className="h-9 px-3 rounded-md border border-velta-border bg-white text-xs font-medium text-velta-mute hover:text-velta-ink hover:border-velta-ink inline-flex items-center gap-1.5 btn-tactile"
            title="Richiama e ristampa l'ultimo documento emesso"
          >
            <Printer className="w-3.5 h-3.5" /> Ristampa ultimo
          </button>
          <StoreSwitcher compact />
          <OperatorSwitcher compact />
        </div>
      </header>
      {topStrip}

      <div className="flex-1 flex overflow-hidden">
        {/* COLONNA 1 — Categorie + Prodotti */}
        <section className="flex-1 min-w-0 p-3 flex flex-col gap-2 overflow-hidden">
          {productsArea || (<>
          <div className="relative shrink-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A1A1AA]" />
            <input
              data-testid={`${tp}-product-search`}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cerca articolo o codice…"
              className="h-12 w-full rounded-xl border border-[#E4E4E7] bg-white pl-10 pr-4 text-sm text-[#18181B] outline-none focus:border-[#B73C27]/40 focus:ring-4 focus:ring-[#B73C27]/10"
            />
          </div>
          {Array.isArray(extras) && extras.length > 0 && (
            <div className="flex gap-1.5 flex-wrap shrink-0">
              {(Array.isArray(extras) ? extras : []).map((ex) => {
                const ExIcon = ex.icon;
                return (
                  <button
                    key={ex.id}
                    data-testid={`${tp}-extra-${ex.id}`}
                    onClick={() => onExtraTap(ex)}
                    className={`h-11 px-3 rounded-md text-sm font-medium inline-flex items-center gap-2 btn-tactile ${
                      kpNumeric > 0 ? "border-2 border-dashed border-emerald-500 bg-emerald-50 text-emerald-800" : "bg-white border border-velta-border text-velta-mute hover:border-velta-ink"
                    }`}
                  >
                    {ExIcon && <ExIcon className="w-4 h-4" />} {ex.label}
                  </button>
                );
              })}
            </div>
          )}
          <div className="flex gap-1.5 flex-wrap shrink-0" title="Importo digitato + categoria = vendita rapida. Importo + articolo = prezzo manuale.">
            {(Array.isArray(cats) ? cats : []).map((c) => (
              <button
                key={c.id}
                data-testid={`${tp}-cat-${c.id}`}
                onClick={() => onCategoryTap(c)}
                className={`h-11 px-4 rounded-md text-sm font-medium btn-tactile ${
                  kpNumeric > 0 && selectedLine == null
                    ? "border-2 border-dashed border-amber-400 bg-amber-50 text-amber-800"
                    : activeCat === c.id
                    ? "text-white"
                    : "bg-white border border-velta-border text-velta-ink hover:border-velta-ink"
                }`}
                style={(kpNumeric <= 0 || selectedLine != null) && activeCat === c.id ? { background: c.color } : {}}
              >
                {c.name}
              </button>
            ))}
          </div>
          <div className="flex-1 overflow-y-auto pos-scroll">
            <div className="grid grid-cols-3 xl:grid-cols-4 gap-2 content-start">
              {(Array.isArray(filtered) ? filtered : []).map((p) => {
                const cat = (Array.isArray(cats) ? cats : []).find((c) => c.id === p.category_id);
                const color = p.color || cat?.color || "#0A0A0A";
                return (
                  <button
                    key={p.id}
                    data-testid={`${tp}-prod-${p.id}`}
                    onClick={() => onProductTap(p)}
                    className={`relative h-20 rounded-md bg-white p-2 text-left flex flex-col justify-between btn-tactile overflow-hidden ${
                      (kpNumeric > 0 && selectedLine == null) || multiplier ? "border-2 border-dashed border-blue-400" : "border border-velta-border hover:border-velta-ink"
                    }`}
                  >
                    <div className="absolute top-0 left-0 right-0 h-1" style={{ background: color }} />
                    <span className="text-xs font-medium leading-tight mt-1.5 line-clamp-2">{p.name}</span>
                    {p.show_price && <span className="font-mono font-bold text-sm">{formatEUR(p.price)}</span>}
                    {p.stock_limit != null && p.stock_current != null && p.stock_current <= p.stock_limit && (
                      <span className="absolute bottom-1 right-1 text-[9px] font-mono uppercase text-amber-700 bg-amber-100 px-1 rounded">scorta {p.stock_current}</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
          </>)}
        </section>

        {/* COLONNA 2 — Tastierino + CL */}
        <div className="w-[300px] shrink-0 p-3 pl-0 flex flex-col gap-2 overflow-y-auto pos-scroll">
          <CheckoutKeypad
            value={kpValue}
            onValueChange={setKpValue}
            multiplier={multiplier}
            onMultiplierChange={setMultiplier}
            onFreePrice={addFreeLine}
            onDiscountPct={applyDiscountPct}
            onDiscountEur={applyDiscountEur}
            selectedLineName={selectedLine != null ? lines[selectedLine]?.name : null}
          />
          <div className="mt-auto pt-2">
            <ClearAllButton onConfirm={clearAll} testid={`${tp}-cl`} />
          </div>
        </div>

        {/* COLONNA 3 — Conto + Pagamenti */}
        <div className="w-[380px] shrink-0 p-3 pl-0 flex flex-col gap-2 overflow-hidden">
          <div className="bg-white border border-velta-border rounded-md flex-1 flex flex-col overflow-hidden min-h-0">
            <div className="px-3 py-2 border-b border-velta-border flex items-center justify-between shrink-0">
              <span className="text-[10px] font-medium uppercase tracking-widest text-[#71717A]">
                Conto — {safeLines.length} {safeLines.length === 1 ? "riga" : "righe"}
              </span>
              <span className="text-[10px] text-[#A1A1AA]" title="Tap: sconto riga. Scorri a sinistra +1, a destra rimuovi.">Scorri per qty</span>
            </div>
            {cartHeaderExtra}
            <div className="flex-1 min-h-0 overflow-y-auto pos-scroll divide-y divide-velta-border">
              {safeLines.length === 0 ? (
                <div className="text-center text-sm text-velta-mute p-8">Aggiungi articoli.</div>
              ) : (
                safeLines.map((l, i) => (
                  <SwipeCartLine
                    key={`${l.product_id || l.name}-${i}`}
                    line={l}
                    index={i}
                    onRemove={removeLine}
                    onIncrement={incrementLine}
                    onTap={onLineTap}
                    selected={selectedLine === i}
                    testidPrefix={`${tp}-cart`}
                  />
                ))
              )}
            </div>
            <GestionaleLongPress orderId={null} total={total} tendered={kpNumeric} saveOrder={saveOrderForGestionale} onClosed={onGestionaleClosed}>
              <div className="border-t-2 border-[#1E2D5B] px-3 py-2 flex items-center justify-between bg-velta-bg shrink-0">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-velta-mute">Totale</div>
                  <div data-testid={`${tp}-cart-count`} className="text-xs font-mono font-semibold">
                    {itemCount} {itemCount === 1 ? "articolo" : "articoli"}
                  </div>
                  {discountPct > 0 && (
                    <div className="text-[10px] font-mono text-[#2F9E77]">Sconto −{discountPct.toFixed(1)}%</div>
                  )}
                  {discountEur > 0 && (
                    <div className="text-[10px] font-mono text-[#0F766E]">Sconto −{formatEUR(discountEur)}</div>
                  )}
                </div>
                <div data-testid={`${tp}-total`} className="font-mono font-bold text-4xl tracking-tighter text-velta-ink">{formatEUR(total)}</div>
              </div>
            </GestionaleLongPress>
          </div>

          <button
            data-testid={`${tp}-crm-toggle`}
            onClick={() => setCrmOpen(!crmOpen)}
            className="h-9 rounded-md bg-white border border-velta-border text-xs font-medium text-velta-mute hover:text-velta-ink flex items-center justify-center gap-2 shrink-0"
          >
            <Star className={`w-3.5 h-3.5 ${customer ? "text-[#2F9E77] fill-current" : ""}`} />
            {customer ? customer.name : "Cliente fedeltà"}
            {lotteryCode && <span className="inline-flex items-center gap-1 font-mono text-[#2F9E77]"><Ticket className="w-3 h-3" /> {lotteryCode}</span>}
            {!lotteryCode && !customer && <span className="text-velta-mute/60">/ Lotteria</span>}
            <ChevronDown className={`w-3.5 h-3.5 transition ${crmOpen ? "rotate-180" : ""}`} />
          </button>
          {crmOpen && (
            <div className="space-y-2 shrink-0">
              {mode !== "lavanderia" && (
              <CustomerLoyaltyPicker selected={customer} onSelect={setCustomer} redeem={redeemPoints} onRedeemChange={setRedeemPoints} testid={`${tp}-loy`} mode={mode} />
              )}
              <LotteryCodeInput value={lotteryCode} onChange={setLotteryCode} testid={`${tp}-lottery`} />
            </div>
          )}

          <div className="shrink-0">
            <CheckoutPayments
              total={total}
              disabled={!safeLines.length}
              onPay={close}
              customMethods={tenant?.custom_payment_methods || []}
              testidPrefix={tp}
            >
              <PrecontoButton
                mode={mode}
                lines={lines}
                discountPct={discountPct}
                discountEur={discountEur}
                total={total}
                tendered={kpNumeric}
                tenant={tenant}
                notes={saleNote || undefined}
                orderId={precontoOrderId}
                onOrderSaved={setPrecontoOrderId}
                testid={`${tp}-preconto-btn`}
                className="h-12 rounded-md bg-white border-2 border-[#B45309] text-[#B45309] hover:bg-[#B45309] hover:text-white text-sm font-medium flex items-center justify-center gap-1.5 btn-tactile disabled:opacity-40"
              />
            </CheckoutPayments>
          </div>
        </div>
      </div>

      {showScontrino && <ScontrinoPreview order={last} tenant={tenant} fiscal onClose={() => setShowScontrino(false)} />}
      {reprint && <ScontrinoPreview order={reprint.order} tenant={tenant} fiscal={reprint.fiscal} onClose={() => setReprint(null)} />}
      {lowPopup && <LowStockPopup items={lowPopup} onClose={() => setLowPopup(null)} />}
    </div>
  );
}

export default function POSNegozioSafe(props) {
  return (
    <ErrorBoundary>
      <POSNegozio {...props} />
    </ErrorBoundary>
  );
}
